import React from 'react';
import PropTypes from 'prop-types';
import {
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED,
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_TO_COLOR_MAPPING,
} from 'common/applicationAssemblyLineConstants';

import { TENANT_SCREENING_CRITERIA_CREDIT_SCORE_QUALIFICATION_AVERAGE } from 'common/tenantScreeningCriteriaConstants';

import { getHouseholdCreditScoreAverage } from 'common/householdHelpers';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrErrorMessage, {
  SR_ERROR_MESSAGE_THEME_ORANGE,
  SR_ERROR_MESSAGE_THEME_RED,
} from 'client/components/SrErrorMessage/SrErrorMessage';
import SrInfoTile, { SR_INFO_TILE_THEME_ADMIN_GREY } from 'client/components/SrInfoTile/SrInfoTile';

function getCreditScores({ assemblyLine, householdApplication }) {
  const requiredCreditScore = assemblyLine.tenantScreeningCriteria?.creditScore;
  const requiredCreditScoreQualification =
    assemblyLine.tenantScreeningCriteria?.creditScoreQualification;

  const tenantAlertReport = assemblyLine.tenantAlertReports
    ? assemblyLine.tenantAlertReports.find((tar) => tar.userId === householdApplication.userId)
    : null;

  const useAverageCreditScore =
    requiredCreditScoreQualification ===
    TENANT_SCREENING_CRITERIA_CREDIT_SCORE_QUALIFICATION_AVERAGE;

  const creditScore = tenantAlertReport?.creditScore;
  const creditScoreAverage = useAverageCreditScore
    ? getHouseholdCreditScoreAverage({
        guarantorApplications: assemblyLine.guarantorApplications,
        tenantAlertReports: assemblyLine.tenantAlertReports,
        householdApplications: assemblyLine.householdApplications,
      })
    : null;

  const isCreditScoreOk = useAverageCreditScore
    ? creditScoreAverage >= requiredCreditScore
    : creditScore >= requiredCreditScore;

  return {
    creditScore,
    creditScoreAverage,
    requiredCreditScore,
    isCreditScoreOk,
    tenantAlertReport,
    useAverageCreditScore,
  };
}

const SrApplicationAssemblyLineStepCreditScoreErrorMessage = ({
  status,
  tenantAlertReport,
  creditScore,
}) => {
  if (
    status === SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED ||
    status === SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED
  ) {
    return null;
  }

  const isNotQualified = status === SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED;

  return (
    <SrBox marginTop="22px">
      <SrText
        color={isNotQualified ? 'red' : 'orange300'}
        font="greycliff"
        weight="700"
        size="18"
        lineHeight="24px"
        display="block"
      >
        Information Required
      </SrText>
      <SrBox marginTop="8px" maxWidth="468px">
        <SrErrorMessage
          theme={isNotQualified ? SR_ERROR_MESSAGE_THEME_RED : SR_ERROR_MESSAGE_THEME_ORANGE}
        >
          Applicant Credit Score
          <br />
          {tenantAlertReport && !creditScore
            ? "The applicant's credit score was not found. Please re-run their tenant alert."
            : "The applicant's credit score doesn't meet the requirements. Please ask the applicant to provide " +
              'a Guarantor with their application and give them instructions on how to do so. If the credit score is close please contact your Associate Account Manager.'}
        </SrErrorMessage>
        <SrBox marginTop="16px" />
        <SrText
          color={isNotQualified ? 'red' : 'orange300'}
          font="greycliff"
          weight="600"
          size="16"
          lineHeight="24px"
        >
          {tenantAlertReport && !creditScore
            ? 'If you have re-run their tenant alert, and the credit score is still not found, ' +
              'please post in the #QA Slack channel and link this household.'
            : "Once you've asked the applicant for these changes move on to the " +
              'next applicant in this household.'}
        </SrText>
      </SrBox>
    </SrBox>
  );
};

const SrApplicationAssemblyLineStepCreditScore = ({ assemblyLine, householdApplication }) => {
  const {
    requiredCreditScore,
    creditScore,
    creditScoreAverage,
    tenantAlertReport,
    useAverageCreditScore,
  } = getCreditScores({ assemblyLine, householdApplication });

  const status = SrApplicationAssemblyLineStepCreditScore.getStatus({
    assemblyLine,
    householdApplication,
  });

  const creditScoreDom = (
    <SrBox display="inlineBlock">
      {useAverageCreditScore && (
        <SrBox marginBottom="20px">
          <SrText font="greycliff" fontSize="14px" weight="600" lineHeight="20px">
            This listing uses the average credit score of all applicants to qualify the household.
            Roommates with higher credit can be added to help qualify the household. If a guarantor
            is added, only their credit is considered to qualify.
          </SrText>
        </SrBox>
      )}
      <SrFlex border="1px solid #EAEBEC" borderRadius="8px" padding="16px">
        {useAverageCreditScore && (
          <>
            <SrInfoTile
              theme={SR_INFO_TILE_THEME_ADMIN_GREY}
              label="Avg of All Applicants Score"
              value={creditScoreAverage}
              valueColor={SR_APPLICATION_ASSEMBLY_LINE_STATUS_TO_COLOR_MAPPING[status]}
            />
            <SrBox height="35px" width="1px" backgroundColor="#EAEBEC" margin="0 24px" />
          </>
        )}
        <SrInfoTile
          theme={SR_INFO_TILE_THEME_ADMIN_GREY}
          label="Applicant Credit Score"
          value={
            tenantAlertReport ? creditScore || 'Credit Score Not Found' : 'Tenant Alert Not Ran'
          }
          valueColor={
            useAverageCreditScore
              ? null
              : SR_APPLICATION_ASSEMBLY_LINE_STATUS_TO_COLOR_MAPPING[status]
          }
        />
        <SrBox height="35px" width="1px" backgroundColor="#EAEBEC" margin="0 24px" />
        <SrInfoTile
          theme={SR_INFO_TILE_THEME_ADMIN_GREY}
          label="Required Credit Score"
          value={requiredCreditScore ? `${requiredCreditScore}+` : '--'}
        />
      </SrFlex>
    </SrBox>
  );

  return (
    <SrBox minWidth="300px">
      {creditScoreDom}
      <SrApplicationAssemblyLineStepCreditScoreErrorMessage
        status={status}
        tenantAlertReport={tenantAlertReport}
        creditScore={creditScore}
      />
    </SrBox>
  );
};

SrApplicationAssemblyLineStepCreditScore.propTypes = {
  assemblyLine: PropTypes.object.isRequired,
  householdApplication: PropTypes.object.isRequired,
};

SrApplicationAssemblyLineStepCreditScore.getStepTitle = ({ assemblyLine }) => {
  if (
    assemblyLine.tenantScreeningCriteria?.creditScoreQualification ===
    TENANT_SCREENING_CRITERIA_CREDIT_SCORE_QUALIFICATION_AVERAGE
  ) {
    // eslint-disable-line max-len
    return 'Credit Score (Average of All Applicants)';
  }

  return 'Credit Score';
};
SrApplicationAssemblyLineStepCreditScore.displayName = 'SrApplicationAssemblyLineStepCreditScore';

SrApplicationAssemblyLineStepCreditScore.getStatus = ({ assemblyLine, householdApplication }) => {
  const { tenantAlertReport, isCreditScoreOk } = getCreditScores({
    assemblyLine,
    householdApplication,
  });

  let status = SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_STARTED;

  if (tenantAlertReport) {
    status = isCreditScoreOk
      ? SR_APPLICATION_ASSEMBLY_LINE_STATUS_QUALIFIED
      : SR_APPLICATION_ASSEMBLY_LINE_STATUS_NOT_QUALIFIED;
  }

  return status;
};

export default SrApplicationAssemblyLineStepCreditScore;
